import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Close as CloseIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { Lead } from '../../types';
import { formatDateTimeIst } from '../../utils/dateUtils';

const LeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    search: '',
    date_from: '',
    date_to: '',
  });
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    total: 0,
  });

  const fetchLeads = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const params: any = {
        per_page: pagination.pageSize,
        page: pagination.page + 1,
      };
      
      if (filters.status) params.status = filters.status;
      if (filters.source) params.source = filters.source;
      if (filters.search) params.search = filters.search;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const response = await apiService.getAdminLeads(params);
      setLeads(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
      }));
    } catch (err: any) {
      console.error('Error fetching leads:', err);
      setError(err.response?.data?.message || 'Failed to load leads');
      setLeads([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    setPagination(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [filters]);

  const handleRowClick = async (params: any) => {
    try {
      setDetailsLoading(true);
      setOpenDetails(true);
      setSelectedLead(null);
      const lead = await apiService.getAdminLead(params.row.id);
      setSelectedLead(lead);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load lead details');
      setOpenDetails(false);
    }
    finally {
      setDetailsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'default';
      case 'requested_slot': return 'info';
      case 'in_discussion': return 'warning';
      case 'scheduled': return 'primary';
      case 'closed_won': return 'success';
      case 'closed_lost': return 'error';
      default: return 'default';
    }
  };

  const formatStatus = (status?: string | null) => {
    if (!status || typeof status !== 'string') return 'N/A';
    return status
      .split('_')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const columns: GridColDef[] = [
    {
      field: 'lead_reference',
      headerName: 'Reference',
      width: 150,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <AssignmentIcon fontSize="small" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'professional',
      headerName: 'Professional',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon fontSize="small" />
          <Typography variant="body2">
            {params.row.professional?.name || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'patient',
      headerName: 'Patient',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.patient?.name || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={formatStatus(params.value as any)}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={formatStatus(params.value as any)}
          size="small"
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'response_time_hours',
      headerName: 'Response Time',
      width: 150,
      renderCell: (params) => {
        if (!params.value) return <Typography variant="body2">No response</Typography>;
        const isFast = params.value < 2;
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <AccessTimeIcon fontSize="small" color={isFast ? 'success' : 'warning'} />
            <Typography variant="body2" color={isFast ? 'success.main' : 'warning.main'}>
              {params.value.toFixed(1)}h
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      renderCell: (params) => new Date(params.value).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Lead Monitoring</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Search"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Reference, name..."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="requested_slot">Requested Slot</MenuItem>
                  <MenuItem value="reschedule_requested">Reschedule Requested</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="declined">Declined</MenuItem>
                  <MenuItem value="closed_won">Closed Won</MenuItem>
                  <MenuItem value="closed_lost">Closed Lost</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={filters.source}
                  label="Source"
                  onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="contact_reveal">Contact Reveal</MenuItem>
                  <MenuItem value="video_consultation_booking">Video Consultation</MenuItem>
                  <MenuItem value="in_person_booking">In-Person Booking</MenuItem>
                  <MenuItem value="phone_booking">Phone Booking</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={leads}
              columns={columns}
              paginationMode="server"
              paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
              onPaginationModelChange={(model) => setPagination(prev => ({ ...prev, page: model.page, pageSize: model.pageSize }))}
              pageSizeOptions={[10, 25, 50, 100]}
              rowCount={pagination.total}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick
              autoHeight
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="start" justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h5" fontWeight={800} lineHeight={1.1}>
                Lead Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedLead?.lead_reference ? `#${selectedLead.lead_reference}` : 'Fetching details…'}
              </Typography>
              {!detailsLoading && selectedLead && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={formatStatus(selectedLead.status)}
                    color={getStatusColor(selectedLead.status)}
                    size="small"
                  />
                  <Chip
                    label={formatStatus(selectedLead.source)}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                  {selectedLead.is_fast_responder && (
                    <Chip label="Fast Responder" color="success" size="small" variant="outlined" />
                  )}
                </Box>
              )}
            </Box>
            <IconButton onClick={() => setOpenDetails(false)} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }} color="text.secondary">
                Loading lead details…
              </Typography>
            </Box>
          ) : selectedLead ? (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                {/* Left: Lead + timing */}
                <Grid size={{ xs: 12, md: 7 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Lead
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Reference
                        </Typography>
                        <Typography variant="body1" fontWeight={700}>
                          {selectedLead.lead_reference || 'N/A'}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Service
                        </Typography>
                        <Typography variant="body1" fontWeight={700}>
                          {selectedLead.professional_service?.service_name || 'N/A'}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Created
                        </Typography>
                        <Typography variant="body1">
                          {selectedLead.created_at ? formatDateTimeIst(selectedLead.created_at) : 'N/A'}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Contact Revealed
                        </Typography>
                        <Typography variant="body1">
                          {selectedLead.contact_revealed_at
                            ? formatDateTimeIst(selectedLead.contact_revealed_at)
                            : '—'}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          First Response
                        </Typography>
                        <Typography variant="body1">
                          {selectedLead.first_response_at ? formatDateTimeIst(selectedLead.first_response_at) : '—'}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" color="text.secondary">
                          Response Time
                        </Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTimeIcon fontSize="small" />
                          <Typography
                            variant="body1"
                            fontWeight={700}
                            color={
                              selectedLead.response_time_hours != null
                                ? selectedLead.response_time_hours < 2
                                  ? 'success.main'
                                  : 'warning.main'
                                : 'text.secondary'
                            }
                          >
                            {selectedLead.response_time_hours != null
                              ? `${selectedLead.response_time_hours.toFixed(1)} hours`
                              : 'No response'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  {(selectedLead.notes || selectedLead.close_reason) && (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                      <Typography variant="subtitle1" fontWeight={800}>
                        Notes
                      </Typography>
                      <Divider sx={{ my: 1.5 }} />
                      {selectedLead.notes && (
                        <Box sx={{ mb: selectedLead.close_reason ? 1.5 : 0 }}>
                          <Typography variant="caption" color="text.secondary">
                            Notes
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {selectedLead.notes}
                          </Typography>
                        </Box>
                      )}
                      {selectedLead.close_reason && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Close Reason
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {selectedLead.close_reason}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  )}
                </Grid>

                {/* Right: Professional + Patient */}
                <Grid size={{ xs: 12, md: 5 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Professional
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="body1" fontWeight={700}>
                      {selectedLead.professional?.name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedLead.professional?.email || 'N/A'}
                    </Typography>
                    {selectedLead.professional?.id && (
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        endIcon={<LaunchIcon />}
                        onClick={() => {
                          sessionStorage.setItem('admin_open_user_type', 'professional');
                          sessionStorage.setItem('admin_open_user_id', String(selectedLead.professional?.id));
                          navigate('/professionals');
                        }}
                      >
                        View profile
                      </Button>
                    )}
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Patient
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="body1" fontWeight={700}>
                      {selectedLead.patient?.name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedLead.patient?.email || 'N/A'}
                    </Typography>
                    {selectedLead.patient?.id && (
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        endIcon={<LaunchIcon />}
                        onClick={() => {
                          sessionStorage.setItem('admin_open_user_type', 'patient');
                          sessionStorage.setItem('admin_open_user_id', String(selectedLead.patient?.id));
                          navigate('/patients');
                        }}
                      >
                        View profile
                      </Button>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box py={4}>
              <Alert severity="warning">No lead details found.</Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadsPage;

